import React from 'react';
import { useGameLogic, useQuestions } from '../hooks';
import { ErrorBoundary } from './ErrorBoundary';
import { FloatingResetButton } from './FloatingResetButton';
import { GameArea } from './GameArea';
import { LevelsPanel } from './LevelsPanel';
import { GameOverlay } from './GameOverlay';
import { Loading } from './Loading';
import { MuteToggleButton } from './MuteToggleButton';
import { getPhaseLadderShortages } from '../utils/phaseLadderValidation';

export const MillionaireGame: React.FC = () => {
  const [showCountdown, setShowCountdown] = React.useState(false); // Fix: Start hidden
  const {
    status,
    gamePhases,
    gameActualQuestion,
    answerFeedback,
    isWin,
    isGameOver,
    selectOption,
    pressResolve,
    pressReset,
    pressStart,
    pressNext,
    renamePhase,
    increasePhaseDifficulty,
    reducePhaseDifficulty,
    increasePhases,
    reducePhases,
    handleHelpItem,
  } = useGameLogic();

  const {
    easyQuestions,
    mediumQuestions,
    hardQuestions,
    actualQuestion,
    loading,
    error,
  } = useQuestions({ questionId: status.question });

  const phaseLadderShortages = React.useMemo(
    () => getPhaseLadderShortages(gamePhases, {
      0: easyQuestions.length,
      1: mediumQuestions.length,
      2: hardQuestions.length,
    }),
    [gamePhases, easyQuestions.length, mediumQuestions.length, hardQuestions.length]
  );
  const hasPhaseLadderShortage = phaseLadderShortages.length > 0;

  // Stabilized with useCallback so GameArea (and its memoized subtree) doesn't
  // re-render just because an unrelated state update (e.g. typing a phase name)
  // re-ran this component and would otherwise recreate these as new closures.
  const handleStartGame = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (hasPhaseLadderShortage) return;
    pressStart(e, easyQuestions, mediumQuestions, hardQuestions);
  }, [hasPhaseLadderShortage, pressStart, easyQuestions, mediumQuestions, hardQuestions]);

  const handleResolve = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    pressResolve(e, actualQuestion);
  }, [pressResolve, actualQuestion]);

  const handleNext = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    pressNext(e, easyQuestions, mediumQuestions, hardQuestions);
  }, [pressNext, easyQuestions, mediumQuestions, hardQuestions]);

  const handleResetGame = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setShowCountdown(false); // Fix: Don't show on reset
    pressReset(e, easyQuestions, mediumQuestions, hardQuestions);
  }, [pressReset, easyQuestions, mediumQuestions, hardQuestions]);

  const handleErrorBoundaryReset = React.useCallback(() => {
    setShowCountdown(false);
    pressReset(
      { preventDefault: () => undefined } as React.MouseEvent<HTMLButtonElement>,
      easyQuestions,
      mediumQuestions,
      hardQuestions,
    );
  }, [pressReset, easyQuestions, mediumQuestions, hardQuestions]);

  const handleUseHelpItem = React.useCallback((itemIndex: number) => {
    if (itemIndex === 0) { // Index 0 is Phone/Llamada
      setShowCountdown(true);
    }
    handleHelpItem(itemIndex, easyQuestions, mediumQuestions, hardQuestions);
  }, [handleHelpItem, easyQuestions, mediumQuestions, hardQuestions]);

  if (loading && easyQuestions.length === 0) {
    return <Loading />;
  }

  if (error) {
    return <div>Error loading questions!</div>;
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="ernarvi-logo">ERNARVI</div>
        <h1 className="app-title">BETIS <span>MILLONARIO</span></h1>
        <div className="text-white font-medium" style={{ opacity: 0.8 }}>ESTADIO BENITO VILLAMARÍN</div>
      </header>

      <GameOverlay
        isWin={isWin}
        isGameOver={isGameOver}
        onReset={handleResetGame}
      />

      <main className="app-main">
        <section className="game-section">
          <ErrorBoundary
            key={status.phase}
            title="Pregunta no disponible"
            message="Esta pregunta no se ha podido mostrar correctamente. Usa el botón de reinicio para continuar con una nueva partida."
            resetLabel="Reintentar"
            onReset={handleErrorBoundaryReset}
          >
            <GameArea
              status={status}
              gameActualQuestion={gameActualQuestion}
              actualQuestion={actualQuestion}
              answerFeedback={answerFeedback}
              title={gamePhases[status.phase]?.name || (status.phase >= 0 ? `PREGUNTA ${status.phase + 1}` : "")}
              onSelectOption={selectOption}
              onStart={handleStartGame}
              onResolve={handleResolve}
              onNext={handleNext}
              onReset={handleResetGame}
              onUseHelpItem={handleUseHelpItem}
              showCountdown={showCountdown}
              setShowCountdown={setShowCountdown}
              startDisabled={hasPhaseLadderShortage}
            />
          </ErrorBoundary>
        </section>

        <aside className="levels-section">
          <LevelsPanel
            gamePhases={gamePhases}
            status={status}
            onRenamePhase={renamePhase}
            onIncreasePhaseDifficulty={increasePhaseDifficulty}
            onReducePhaseDifficulty={reducePhaseDifficulty}
            onIncreasePhases={increasePhases}
            onReducePhases={reducePhases}
            phaseLadderShortages={phaseLadderShortages}
          />
        </aside>
      </main>

      <FloatingResetButton
        onReset={handleResetGame}
        isVisible={status.step !== "start"}
      />

      <MuteToggleButton />
    </div>
  );
};
